import React, { useState } from 'react';
import HGSpinner from 'components/HGSpinner';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import { useDeviceState } from '../service';
import UpgradeFirmwareButton from '../UpgradeFirmwareButton';
import { syncDevice } from './helper';
import { Button } from 'components/shadcn/ui/button';
import { useJwtToken } from 'helper';
import { Alert, AlertDescription } from 'components/shadcn/ui/alert';

const StackData = ({ device }) => {
    const accessToken = useJwtToken();
    const dataLabels = {
        hw_ver: 'Hardware Version',
        fw_ver: 'Software Version',
        E_hw_ver: 'EMS Hardware Version',
        E_fw_ver: 'EMS Software Version',
        protocolId: 'Protocol ID',
        minTempPos: 'Min Temp. Position',
        maxTempPos: 'Max Temp. Position',
        minVoltPos: 'Min Volt Position',
        maxVoltPos: 'Max Volt Position',
        dcTemp: 'DC Temp',
        MOSTemp: 'MOS Temp',
        runState: 'Run State',
        onlinePackQuantity: 'Online Pack Quantity',
    };
    const { isLoading, data: deviceState } = useDeviceState();
    const isOnline = deviceState?.isOnline || 'offline';
    const emsUpgradeState = deviceState?.emsUpgradeState;
    const needsToBeSynched = device.isLegacy && !device.isSynched;

    const [syncStatus, setSyncStatus] = useState('idle');
    const error = syncStatus.includes('failed');

    //create a function to sync device and set to loading
    const handleSync = async () => {
        setSyncStatus('syncing');
        try {
            await syncDevice(device, accessToken);
            setSyncStatus('success');
        } catch (error) {
            setSyncStatus(`Syncing failed. ${error.message}`);
        }
    };

    return (
        <div className="p-5">
            <div className="mb-2 flex flex-col items-center gap-5 lg:flex-row">
                <UpgradeFirmwareButton
                    upgradeState={emsUpgradeState}
                    online={isOnline}
                />
                {needsToBeSynched && (
                    <Alert
                        className="flex w-max min-w-[300px] items-center gap-5 rounded-md border-none p-3"
                        variant={
                            syncStatus === 'success'
                                ? 'success'
                                : error
                                  ? 'error'
                                  : 'secondary'
                        }
                    >
                        <AlertDescription
                            className={`flex items-center gap-2 text-base ${
                                syncStatus === 'success'
                                    ? 'text-green-500'
                                    : error
                                      ? 'text-red-500'
                                      : 'text-secondary'
                            }`}
                        >
                            {syncStatus === 'idle' &&
                                'You have some historical data that needs to be transferred.'}
                            {syncStatus === 'syncing' && (
                                <>
                                    <HGSpinner className="ml-2 min-h-0" />
                                    Syncing...
                                </>
                            )}
                            {syncStatus === 'success' && 'Sync successful!'}
                            {error && syncStatus}
                            {(syncStatus === 'idle' || error) && (
                                <Button
                                    customClasses=" h-[40px] w-[150px]"
                                    onClick={handleSync}
                                    variant={error ? 'outline' : 'primary'}
                                >
                                    {error ? 'Try Again' : 'Transfer Data'}
                                </Button>
                            )}
                        </AlertDescription>
                    </Alert>
                )}
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-5">
                {isLoading ? (
                    Object.keys(dataLabels).map((key) => (
                        <Skeleton key={key} className="h-6 w-full" />
                    ))
                ) : !deviceState ||
                  (Object.keys(deviceState).length === 0 &&
                      !needsToBeSynched) ? (
                    <h2 className="col-span-2 text-center text-secondary lg:col-span-3">
                        No data available
                    </h2>
                ) : (
                    Object.entries(dataLabels).map(([key, value]) => (
                        <div
                            key={key}
                            className="flex w-full items-center justify-start overflow-hidden"
                        >
                            <h6 className="whitespace-nowrap text-base font-medium">
                                {value}:
                            </h6>
                            <p className="ml-2 text-base font-light">
                                {deviceState?.[key] || 'N/A'}
                            </p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default StackData;
